import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePersonaStore } from '@/stores/personaStore';
import { initSession } from '@/api/session';
import { refresh, mapRefreshUser } from '@/api/auth';
import type { User } from '@/stores/authStore';

/**
 * How often (ms) to re-call `/auth/refresh` while a tab is open. Picks
 * up server-side role/tier changes mid-session without forcing the user
 * to log out and back in. 10 minutes is conservative — short enough that
 * promotions feel "near-real-time" but long enough to avoid request
 * spam from a chatty SPA.
 */
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Protects all child routes behind authentication.
 * If no valid token exists, redirects to /auth.
 *
 * Calls `/auth/refresh` on every mount AND on a periodic interval so
 * server-side changes to roles/tier/persona/credits propagate into the
 * FE store without requiring a logout-login cycle. The previous
 * version only refreshed when the in-memory user was missing, which
 * meant a stale cached user (e.g. roles minted before a promotion)
 * could persist forever in localStorage. See routes/refresh.ts in
 * auth-svc for the BQ re-read logic.
 */
export function AuthGuard() {
  const token = useAuthStore((s) => s.token);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setCurrentPersona = usePersonaStore((s) => s.setCurrentPersona);
  const initedRef = useRef(false);
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const runRefresh = async () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      try {
        const res = await refresh();
        if (cancelled) return;

        if (!res.user) {
          // User record was deleted on the server — sign out locally.
          clearAuth();
          return;
        }

        setAuth(res.token, mapRefreshUser(res.user) as User);
      } catch {
        // Refresh is best-effort. The api/client.ts isBestEffort guard
        // prevents 401s here from triggering the global logout-redirect;
        // the next protected-route navigation will surface auth state.
      } finally {
        refreshingRef.current = false;
      }
    };

    // Immediate refresh on mount so we replace any stale cached user
    // with the freshest BQ-backed copy before the user navigates.
    runRefresh();

    // Periodic re-check while the tab is open. Keeps long-lived sessions
    // in sync with server-side role / tier / credit changes.
    const intervalId = window.setInterval(runRefresh, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [token, setAuth, clearAuth]);

  useEffect(() => {
    if (!token || initedRef.current) return;
    initedRef.current = true;
    initSession()
      .then((ctx) => {
        if (ctx.personaId) setCurrentPersona(ctx.personaId);
      })
      .catch(() => {
        /* First-load 401/404 is non-fatal; session-backed persistence
           simply won't apply until next init succeeds. */
      });
  }, [token, setCurrentPersona]);

  // No token at all — redirect to auth
  if (!token) {
    return <Navigate to="/auth" replace />;
  }

  // Token exists — check if it's expired
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000;
    if (Date.now() > expiresAt) {
      // Token expired — clear and redirect
      useAuthStore.getState().clearAuth();
      return <Navigate to="/auth" replace />;
    }
  } catch {
    // Malformed token — clear and redirect
    useAuthStore.getState().clearAuth();
    return <Navigate to="/auth" replace />;
  }

  // Authenticated — render child routes
  return <Outlet />;
}
